import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity, useLocaleState, useTranslate } from "ra-core";
import { Link } from "react-router";

import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { RenewalStatus } from "./renewal";
import { formatEuros } from "./format";

const STALE_DAYS = 90;
const DAY = 86_400_000;

interface PortfolioRow {
  id: number;
  name: string;
  renewal_status: RenewalStatus | null;
  contract_amount_current: number | null;
  contract_amount_previous: number | null;
  contract_items_current: string | null;
  last_company_note_at: string | null;
}

interface TaskRow {
  id: number;
  text: string | null;
  type: string | null;
  due_date: string | null;
  company_id: number | null;
  contact_id: number | null;
  companies: { name: string } | null;
}

interface NoteRow {
  id: number;
  date: string;
  kind: string | null;
  text: string | null;
  company_id: number;
  companies: { name: string } | null;
}

/** Everything the home page needs, scoped by RLS to what the user may see. */
const useMyDayData = (saleId?: number | string) =>
  useQuery({
    queryKey: ["my-day", saleId],
    enabled: saleId != null,
    queryFn: async () => {
      const client = getSupabaseClient();
      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 999);
      const [portfolio, tasks, notes] = await Promise.all([
        client
          .from("companies_summary")
          .select(
            "id,name,renewal_status,contract_amount_current,contract_amount_previous,contract_items_current,last_company_note_at",
          ),
        client
          .from("tasks")
          .select("id,text,type,due_date,company_id,contact_id,companies(name)")
          .is("done_date", null)
          .eq("sales_id", saleId!)
          .lte("due_date", endOfToday.toISOString())
          .order("due_date")
          .limit(20),
        client
          .from("company_notes")
          .select("id,date,kind,text,company_id,companies(name)")
          .order("date", { ascending: false })
          .limit(8),
      ]);
      for (const r of [portfolio, tasks, notes]) if (r.error) throw r.error;
      return {
        portfolio: (portfolio.data ?? []) as PortfolioRow[],
        tasks: (tasks.data ?? []) as unknown as TaskRow[],
        notes: (notes.data ?? []) as unknown as NoteRow[],
      };
    },
  });

/**
 * Home page "My day": the money at stake and who to call today, instead of
 * Atomic's generic dashboard.
 */
export const MyDay = () => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { identity } = useGetIdentity();
  const { data, isPending, error } = useMyDayData(identity?.id);
  const year = new Date().getFullYear();

  if (!identity) return null;

  const firstName = String(identity.fullName ?? "").split(" ")[0];
  const today = new Date();
  const daysToYearEnd = Math.ceil(
    (new Date(year, 11, 31).getTime() - today.getTime()) / DAY,
  );

  const portfolio = data?.portfolio ?? [];
  const sum = (rows: PortfolioRow[], key: keyof PortfolioRow) =>
    rows.reduce((total, r) => total + Number(r[key] ?? 0), 0);
  const contracted = portfolio.filter(
    (r) => Number(r.contract_amount_current) > 0,
  );
  const pending = portfolio
    .filter((r) => r.renewal_status === "pending")
    .sort(
      (a, b) =>
        Number(b.contract_amount_previous) - Number(a.contract_amount_previous),
    );
  const down = portfolio.filter((r) => r.renewal_status === "down");
  const stale = portfolio.filter(
    (r) =>
      r.last_company_note_at &&
      today.getTime() - new Date(r.last_company_note_at).getTime() >
        STALE_DAYS * DAY,
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          {translate("crm.construnario.my_day.greeting", { name: firstName })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {today.toLocaleDateString(locale, {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}{" "}
          ·{" "}
          {translate("crm.construnario.my_day.days_left", {
            smart_count: daysToYearEnd,
            next: year + 1,
          })}
        </p>
      </div>

      {error ? (
        <p className="text-sm text-bad">{String((error as Error).message)}</p>
      ) : null}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Kpi
          label={translate("crm.construnario.my_day.contracted", { year })}
          value={
            isPending
              ? "…"
              : formatEuros(sum(contracted, "contract_amount_current"))
          }
          foot={translate("crm.construnario.my_day.contracted_foot", {
            count: contracted.length,
            total: portfolio.length,
          })}
        />
        <Kpi
          alert={pending.length > 0}
          label={translate("crm.construnario.my_day.pending", {
            previous: year - 1,
          })}
          value={
            isPending
              ? "…"
              : formatEuros(sum(pending, "contract_amount_previous"))
          }
          foot={translate("crm.construnario.my_day.pending_foot", {
            smart_count: pending.length,
          })}
        />
        <Kpi
          label={translate("crm.construnario.my_day.down")}
          value={isPending ? "…" : String(down.length)}
          foot={
            down.length
              ? formatEuros(
                  sum(down, "contract_amount_current") -
                    sum(down, "contract_amount_previous"),
                )
              : translate("crm.construnario.my_day.down_none")
          }
        />
        <Kpi
          label={translate("crm.construnario.my_day.stale", {
            days: STALE_DAYS,
          })}
          value={isPending ? "…" : String(stale.length)}
          foot={
            stale.length
              ? translate("crm.construnario.my_day.stale_foot")
              : translate("crm.construnario.my_day.stale_none")
          }
        />
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          <Panel
            title={translate("crm.construnario.my_day.pending_list")}
            subtitle={translate("crm.construnario.my_day.pending_list_sub", {
              previous: year - 1,
              year,
            })}
            action={
              <Link
                to={`/companies?filter=${encodeURIComponent(JSON.stringify({ renewal_status: "pending" }))}`}
                className="rounded-lg border border-border px-2.5 py-1 text-[13px] font-medium text-foreground no-underline hover:border-muted-foreground"
              >
                {translate("crm.construnario.my_day.see_in_list")}
              </Link>
            }
          >
            {pending.length === 0 ? (
              <Empty
                text={translate("crm.construnario.my_day.pending_empty", {
                  previous: year - 1,
                })}
              />
            ) : (
              pending.map((r) => (
                <Row
                  key={r.id}
                  to={`/companies/${r.id}/show/contracts`}
                  title={r.name}
                  subtitle={translate("crm.construnario.my_day.last_note", {
                    when: relative(r.last_company_note_at, locale),
                  })}
                  end={
                    <span className="font-semibold tabular-nums">
                      {formatEuros(r.contract_amount_previous)}
                    </span>
                  }
                />
              ))
            )}
          </Panel>

          <Panel title={translate("crm.construnario.my_day.recent_notes")}>
            {(data?.notes ?? []).map((n) => (
              <Row
                key={n.id}
                to={`/companies/${n.company_id}/show`}
                lead={new Date(n.date).toLocaleDateString(locale, {
                  day: "numeric",
                  month: "short",
                })}
                title={n.companies?.name ?? ""}
                subtitle={(n.text ?? "")
                  .replace(/<br\s*\/?>/gi, " ")
                  .slice(0, 140)}
                end={
                  n.kind ? (
                    <Tag>
                      {translate(
                        `crm.construnario.company_notes.kind.${n.kind}`,
                      )}
                    </Tag>
                  ) : null
                }
              />
            ))}
          </Panel>
        </div>

        <FollowUps tasks={data?.tasks ?? []} saleId={identity.id} />
      </div>
    </div>
  );
};

const FollowUps = ({
  tasks,
  saleId,
}: {
  tasks: TaskRow[];
  saleId: number | string;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const queryClient = useQueryClient();
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const complete = async (id: number) => {
    await getSupabaseClient()
      .from("tasks")
      .update({ done_date: new Date().toISOString() })
      .eq("id", id);
    await queryClient.invalidateQueries({ queryKey: ["my-day", saleId] });
  };

  return (
    <Panel title={translate("crm.construnario.my_day.follow_ups")}>
      {tasks.length === 0 ? (
        <Empty text={translate("crm.construnario.my_day.follow_ups_empty")} />
      ) : (
        tasks.map((t) => {
          const overdue =
            t.due_date &&
            new Date(t.due_date).getTime() < startOfToday.getTime();
          return (
            <div
              key={t.id}
              className="flex items-start gap-3 border-t border-secondary px-4 py-3 first:border-t-0"
            >
              <button
                type="button"
                onClick={() => complete(t.id)}
                aria-label={translate("crm.construnario.my_day.mark_done")}
                className="mt-0.5 size-4 shrink-0 rounded-[5px] border-[1.5px] border-muted-foreground hover:border-primary"
              />
              <div className="min-w-0 flex-1">
                <div className="line-clamp-2 text-sm font-medium">{t.text}</div>
                <div className="text-[12.5px] text-muted-foreground">
                  {t.company_id ? (
                    <Link
                      to={`/companies/${t.company_id}/show`}
                      className="text-muted-foreground"
                    >
                      {t.companies?.name}
                    </Link>
                  ) : null}
                  {overdue ? (
                    <span className="ml-2 text-bad">
                      {translate("crm.construnario.my_day.overdue", {
                        when: relative(t.due_date, locale),
                      })}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })
      )}
    </Panel>
  );
};

const relative = (value: string | null, locale: string) => {
  if (!value) return "—";
  const days = Math.round((Date.now() - new Date(value).getTime()) / DAY);
  return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
    -days,
    "day",
  );
};

const Kpi = ({
  label,
  value,
  foot,
  alert,
}: {
  label: string;
  value: string;
  foot: string;
  alert?: boolean;
}) => (
  <div
    className={cn(
      "flex flex-col gap-1 rounded-xl border bg-card px-4 py-3.5",
      alert ? "border-bad/35" : "border-border",
    )}
  >
    <span className="text-xs text-muted-foreground">{label}</span>
    <span
      className={cn(
        "text-2xl font-semibold tracking-tight tabular-nums",
        alert && "text-bad",
      )}
    >
      {value}
    </span>
    <span className="text-[12.5px] text-muted-foreground">{foot}</span>
  </div>
);

const Panel = ({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className="rounded-xl border border-border bg-card">
    <header className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-secondary px-4 py-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      {subtitle ? (
        <span className="text-[12.5px] text-muted-foreground">{subtitle}</span>
      ) : null}
      {action ? <span className="ml-auto">{action}</span> : null}
    </header>
    <div>{children}</div>
  </section>
);

const Row = ({
  to,
  lead,
  title,
  subtitle,
  end,
}: {
  to: string;
  lead?: string;
  title: string;
  subtitle?: string;
  end?: React.ReactNode;
}) => (
  <Link
    to={to}
    className="flex items-center gap-3 border-t border-secondary px-4 py-2.5 text-foreground no-underline first:border-t-0 hover:bg-secondary/60"
  >
    {lead ? (
      <span className="w-14 shrink-0 text-xs text-muted-foreground">
        {lead}
      </span>
    ) : null}
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-medium">{title}</span>
      {subtitle ? (
        <span className="block truncate text-[12.5px] text-muted-foreground">
          {subtitle}
        </span>
      ) : null}
    </span>
    {end}
  </Link>
);

const Tag = ({ children }: { children: React.ReactNode }) => (
  <span className="shrink-0 rounded-md border border-border px-1.5 text-[11.5px] font-medium text-muted-foreground">
    {children}
  </span>
);

const Empty = ({ text }: { text: string }) => (
  <p className="px-4 py-3 text-sm text-muted-foreground">{text}</p>
);
